import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Breadcrumbs,
  Grid,
  Link,
  PageHeader,
  Paper,
  Tab,
  Tabs,
  Typography
} from '@esgian/esgianui';
import PortsOverviewSection from '@components/Sections/ShipAnalytics/PortsOverviewSection';
import { getActivePorts, getPortCongestion, getTopPorts } from '@api';
import moment from 'moment';
import OperatorTypeSelect from '@components/Inputs/OperatorTypeSelect/OperatorTypeSelect';
import { DATE_FORMAT, GEOGRAPHICAL_TYPES } from '@constants';
import PortWaitingTimeSection from '@components/Sections/ShipAnalytics/PortProfileSection/SubSections/PortWaitingTimeSection';
import { useSegment } from '@hooks';
import { useSelector } from 'react-redux';
import { getCommercialCategoryType, getOperatorType } from '@store/features';
import { CommercialCategoryTypeSelect } from '@components/Inputs';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';

function PortsOverviewPage() {
  const [disableCongestion, setDisableCongestion] = useState(false);
  const commercialCategoryType = useSelector(getCommercialCategoryType);
  const { isRoRo, segment, segmentMinDate } = useSegment();
  const operatorType = useSelector(getOperatorType);
  const [section, setSection] = useState(1);
  const navigate = useNavigate();

  const handleSectionChange = (_, val) => {
    if (val === 2 && disableCongestion) {
      return;
    }
    setSection(val);
  };

  const routeChange = (path) => {
    navigate(path);
  };

  const portCongestionQuery = useQuery({
    queryKey: [
      'portCongestion',
      {
        GeoType: GEOGRAPHICAL_TYPES.PORT,
        OperatorTypeId: operatorType,
        SegmentTypeId: segment.id,
        CommercialCategoryId: commercialCategoryType
      }
    ],
    enabled: section === 2,
    placeholderData: null,
    queryFn: ({ queryKey, signal }) => {
      return getPortCongestion(
        {
          ...queryKey[1],
          start: moment.utc(segmentMinDate).toISOString(),
          end: moment.utc().toISOString()
        },
        signal
      )
        .then((result) => {
          return result;
        })
        .catch(() => {
          toast.error(`Unable to fetch port congestion data`, {
            position: 'top-right'
          });
          setSection(1);
          setDisableCongestion(true);
          return null;
        });
    }
  });

  return (
    <Grid item container spacing={2}>
      <Grid item xs={12}>
        <PageHeader
          title={'Ports Overview'}
          id={'lbl-port-overview'}
          breadcrumbs={
            <Breadcrumbs aria-label="breadcrumb">
              <Link
                underline="hover"
                color="inherit"
                id="home-breadcrumb"
                sx={{ cursor: 'pointer' }}
                onClick={() =>
                  routeChange(isRoRo ? '/roro/commercial-analytics' : '/mpp/commercial-analytics')
                }>
                Home
              </Link>
              <Link
                sx={{ cursor: 'pointer' }}
                underline="hover"
                color="inherit"
                id="ship-analysis-breadcrumb"
                onClick={() =>
                  routeChange(isRoRo ? '/roro/commercial-analytics' : '/mpp/commercial-analytics')
                }>
                Commercial Analytics
              </Link>
              <Typography color="primary" id="ports-overview-breadcrumb">
                Ports Overview
              </Typography>
            </Breadcrumbs>
          }
        />
      </Grid>

      <Grid item xs={12}>
        {isRoRo ? <OperatorTypeSelect /> : <CommercialCategoryTypeSelect />}
      </Grid>

      <Grid item xs={12} id={'ports-overview'}>
        <Paper>
          <Tabs value={section} onChange={handleSectionChange} variant="scrollable">
            <Tab value={1} label="Ports Overview" />
            <Tab value={2} disabled={disableCongestion} label="Port Waiting Time" />
          </Tabs>
        </Paper>
      </Grid>
      <Grid item xs={12}>
        {section === 1 ? (
          <PortsOverviewSection />
        ) : (
          <PortWaitingTimeSection
            assetLevel={'port'}
            portCongestionData={portCongestionQuery.data}
            loading={portCongestionQuery.isFetching}
          />
        )}
      </Grid>
    </Grid>
  );
}

PortsOverviewPage.propTypes = {};

PortsOverviewPage.defaultProps = {};

export default PortsOverviewPage;
